/* eslint-disable @typescript-eslint/no-shadow */
import {
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  Charge,
  ChargeDTO,
  ChargeStatus,
  CreateChargeInput,
  CreateChargeMutationVariables,
  DateTime,
  DeleteChargeMutationVariables as DeleteMutationVariables,
  Model,
  MutationStatus,
  OnCreateChargeSubscription,
  OnDeleteChargeSubscription,
  OnUpdateChargeSubscription,
  ProcessOcrMutationVariables,
  UpdateChargeInput,
  cleanInputCreate,
  cleanInputUpdate,
  createCharge as createMutation,
  deleteCharge as deleteMutation,
  getChargesFromDto,
  getDateTimeDifference,
  getCharge as getQuery,
  getReadId,
  getTableClientId,
  onCreateCharge,
  onDeleteCharge,
  onUpdateCharge,
  processOcr as processOcrMutation,
  processInChunks,
  removeNestedFieldsExcept,
  syncPostings,
  today,
  todayDateTime,
  updateCharge as updateMutation,
} from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  NUMBER_OF_MINUTES_FOR_REFETCH,
  deleteAndHideEntity,
  get,
  getFilterFieldNameForIndex,
  isConditionalCheckFailedGraphQLError,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import range from 'lodash/range';
import React, { Reducer, useContext, useEffect, useMemo, useReducer, useRef } from 'react';

import { invalidateOpenInvoices } from './openInvoicesInvalidation';
import { fetchPostings, usePostings } from './PostingsContext';
import { clientStatementsQueryKey, fetchChargeStatements, requestStatementRecalculation } from './StatementContext';
import { useUser } from './UserContext';
import { ChargeAffectationPeriod, buildChargeAffectationUpdate } from './utils/chargeAffectationUtils';
import {
  ChargeDeletionBlockedByAffectationError,
  ChargeDeletionBlockedByStatementError,
  getStatementIdsToRecalculate,
  isChargeDeletionBlockedByAffectation,
  isChargeDeletionBlockedByStatement,
  reverseChargePostings,
} from './utils/chargeDeletionUtils';
import { syncCharges, syncChargesForFolders } from './utils/cleanedQueries';

const ENTITY_MODEL_NAME: Model = 'Charge';
const MAX_STATEMENT_RECALCULATION_ROUNDS = 3;
const STATEMENT_RECALCULATION_RETRY_DELAY_MS = 1000;
const CHARGE_DELETION_POSTINGS_QUERY = removeNestedFieldsExcept(
  syncPostings,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  [],
  ['customValues', 'tenantLandings']
);

const waitBeforeStatementRecalculationRetry = () =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, STATEMENT_RECALCULATION_RETRY_DELAY_MS);
  });

export interface ChargeContext extends ChargeState {
  chargesInbox: Charge[];
  chargesToAffect: Charge[];
  chargesArchived: Charge[];
  processOcr: (chargeId: string) => Promise<MutationStatus>;
  getCharge: (id: string) => Charge | undefined;
  createCharge: (input: Omit<CreateChargeInput, 'clientId' | 'readId'>) => Promise<Charge>;
  updateCharge: (updates: UpdateChargeInput) => Promise<Charge>;
  affectCharge: (id: string, period: ChargeAffectationPeriod) => Promise<Charge | null>;
  deleteCharge: (charge: Charge) => Promise<Charge>;
  fetchAndSetCharges: () => Promise<void>;
  setFetchCharges: () => void;
  getChargesLinkedToFile: (fileId: string) => Charge[];
}

interface ChargeState {
  charges: Charge[] | null;
  error: string | undefined;
  chargesLoading: boolean;
  shouldFetch: boolean;
  lastFetch: DateTime | null;
}

export type Action =
  | {
      type: 'SHOULD_FETCH' | 'IS_FETCHING';
    }
  | {
      type: 'FETCH_FAILED';
      payload: { error: string };
    }
  | {
      type: 'ADD_CHARGE';
      payload: { charge: Charge };
    }
  | {
      type: 'FETCHED';
      payload: { charges: Charge[] };
    }
  | {
      type: 'UPDATE_CHARGE';
      payload: { charge: Charge };
    }
  | {
      type: 'DELETE_CHARGE';
      payload: { id: string };
    };

const classifyCharges = (
  charges: Charge[]
): Pick<ChargeContext, 'chargesInbox' | 'chargesToAffect' | 'chargesArchived'> => {
  return charges.reduce(
    (acc: { chargesInbox: Charge[]; chargesToAffect: Charge[]; chargesArchived: Charge[] }, charge) => {
      if (!isNil(charge)) {
        if (charge.status === ChargeStatus.AFFECTED) {
          acc.chargesArchived.push(charge);
        } else if (charge.status === ChargeStatus.TO_AFFECT) {
          acc.chargesToAffect.push(charge);
        } else {
          acc.chargesInbox.push(charge);
        }
      }
      return acc;
    },
    { chargesInbox: [], chargesToAffect: [], chargesArchived: [] }
  );
};

const initialState: ChargeState = {
  chargesLoading: false,
  error: undefined,
  charges: null,
  shouldFetch: false,
  lastFetch: null,
};

export const chargeReducer = (state: ChargeState, action: Action): ChargeState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (
        state.chargesLoading ||
        state.shouldFetch ||
        (state.lastFetch &&
          getDateTimeDifference({ start: state.lastFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetch: true,
      };
    case 'IS_FETCHING':
      return { ...state, chargesLoading: true };
    case 'FETCHED':
      return {
        ...state,
        chargesLoading: false,
        shouldFetch: false,
        charges: action.payload.charges,
        lastFetch: todayDateTime(),
        error: undefined,
      };
    case 'FETCH_FAILED':
      return { ...state, chargesLoading: false, shouldFetch: false, error: action.payload.error };
    case 'ADD_CHARGE':
      if (state.charges?.find((charge) => charge.id === action.payload.charge.id)) {
        return state;
      }

      return {
        ...state,
        charges: [...(state.charges ?? []), action.payload.charge],
      };
    case 'UPDATE_CHARGE':
      if (!state.charges) {
        return state;
      }

      return {
        ...state,
        charges: state.charges.map((charge) => {
          if (charge.id === action.payload.charge.id) {
            return action.payload.charge;
          }
          return charge;
        }),
      };
    case 'DELETE_CHARGE':
      if (!state.charges) {
        return state;
      }
      return {
        ...state,
        charges: state.charges.filter((charge) => charge.id !== action.payload.id),
      };

    default:
      return state;
  }
};

const fetchCharges = async (clientId: string, additionalFilter?: object): Promise<Charge[]> => {
  return await list<Charge>(
    syncCharges,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME),
    additionalFilter
  );
};

export const fetchChargesForFolders = async (clientId: string): Promise<Charge[]> => {
  const charges = await list<ChargeDTO>(
    syncChargesForFolders,
    getFilterFieldNameForIndex('byClientId'),
    getTableClientId(clientId, ENTITY_MODEL_NAME)
  );

  return getChargesFromDto(charges);
};

export const ChargeContext = React.createContext<ChargeContext | null>(null);

export const ChargeContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<ChargeState, Action>>(chargeReducer, initialState);
  const { clientId, isOwner, userId } = useUser();
  const { createPosting, updatePosting } = usePostings();
  const queryClient = useQueryClient();

  const setFetchCharges = () => {
    dispatch({ type: 'SHOULD_FETCH' });
  };

  const fetchAndSetCharges = async () => {
    dispatch({ type: 'IS_FETCHING' });
    try {
      const charges = await fetchCharges(getTableClientId(clientId!, ENTITY_MODEL_NAME));
      dispatch({ type: 'FETCHED', payload: { charges } });
    } catch (error) {
      console.error('[charges] fetching charges failed', error);
      const message = error instanceof Error ? error.message : String(error);
      dispatch({ type: 'FETCH_FAILED', payload: { error: message } });
    }
  };

  useEffect(() => {
    if (state.shouldFetch) fetchAndSetCharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch]);

  const createCharge = async (
    input: CreateChargeInput | Omit<CreateChargeInput, 'clientId' | 'readId'>
  ): Promise<Charge> => {
    let charge: Charge | undefined;
    try {
      charge = await mutation<Charge, CreateChargeMutationVariables>(createMutation, {
        input: {
          ...(cleanInputCreate(input) as CreateChargeInput),
          readId: getReadId(clientId!, ENTITY_MODEL_NAME),
          clientId: getTableClientId(clientId!, ENTITY_MODEL_NAME),
          ...(isOwner && { writers: [userId!] }),
        },
      });
      dispatch({ type: 'ADD_CHARGE', payload: { charge } });
      invalidateOpenInvoices(queryClient, clientId);
    } catch (error) {
      state.error = error as string;
    }
    return charge!;
  };

  useSubscriptions<OnCreateChargeSubscription, OnUpdateChargeSubscription, OnDeleteChargeSubscription>(
    onCreateCharge,
    onUpdateCharge,
    onDeleteCharge,
    (data) => {
      dispatch({
        type: 'ADD_CHARGE',
        payload: { charge: data.onCreateCharge as Charge },
      });
      invalidateOpenInvoices(queryClient, clientId);
    },
    (data) => {
      dispatch({
        type: 'UPDATE_CHARGE',
        payload: { charge: data.onUpdateCharge as Charge },
      });
      invalidateOpenInvoices(queryClient, clientId);
    },
    (data) => {
      const { id } = data.onDeleteCharge as Charge;
      dispatch({
        type: 'DELETE_CHARGE',
        payload: { id },
      });
      invalidateOpenInvoices(queryClient, clientId);
    }
  );

  const processOcr = async (chargeId: string): Promise<MutationStatus> => {
    const result = await mutation<MutationStatus, ProcessOcrMutationVariables>(processOcrMutation, {
      input: {
        chargeId,
        userId: userId ?? '',
        clientId: clientId ?? '',
      },
    });
    return result;
  };

  const getCharge = (id: string) => {
    const charge = (state.charges ?? []).find((chargeElement) => chargeElement.id === id);
    return charge;
  };

  const getChargesLinkedToFile = (fileId: string): Charge[] =>
    (state.charges ?? []).filter((charge) => charge.documentId === fileId);

  const updateCharge = async (updates: UpdateChargeInput): Promise<Charge> => {
    const charge = await mutation<Charge>(updateMutation, {
      input: cleanInputUpdate(updates, false),
    });
    dispatch({ type: 'UPDATE_CHARGE', payload: { charge } });
    invalidateOpenInvoices(queryClient, clientId);
    dispatch({ type: 'SHOULD_FETCH' });
    return charge;
  };

  const affectCharge = async (id: string, period: ChargeAffectationPeriod): Promise<Charge | null> => {
    const writeOn = async (charge: Charge): Promise<Charge | null> => {
      const affectation = buildChargeAffectationUpdate(charge, period, today());
      if (isNil(affectation)) {
        return null;
      }
      const affectedCharge = await mutation<Charge>(updateMutation, {
        input: cleanInputUpdate(affectation, false),
      });
      dispatch({ type: 'UPDATE_CHARGE', payload: { charge: affectedCharge } });
      invalidateOpenInvoices(queryClient, clientId);
      dispatch({ type: 'SHOULD_FETCH' });
      return affectedCharge;
    };
    const chargeInState = getCharge(id);
    if (!isNil(chargeInState?._version)) {
      try {
        return await writeOn(chargeInState);
      } catch (error) {
        if (!isConditionalCheckFailedGraphQLError(error)) {
          throw error;
        }
      }
    }
    return await writeOn(await get<Charge>(getQuery, id));
  };

  const recalculateLinkedDraftStatements = async (chargeId: string): Promise<boolean> => {
    const recalculationRounds = range(MAX_STATEMENT_RECALCULATION_ROUNDS + 1);
    for (const round of recalculationRounds) {
      const chargeStatements = await fetchChargeStatements('byCharge', chargeId);
      const statementIds = getStatementIdsToRecalculate(chargeStatements);
      if (isEmpty(statementIds)) {
        return round > 0;
      }
      if (round === MAX_STATEMENT_RECALCULATION_ROUNDS) {
        break;
      }
      if (round > 0) {
        await waitBeforeStatementRecalculationRetry();
      }
      const recalculations = await processInChunks(
        statementIds,
        (statementId) => requestStatementRecalculation(statementId, userId ?? '', clientId ?? ''),
        1
      );
      if (recalculations.some((recalculation) => !recalculation.status)) {
        throw new Error('A statement could not be recalculated, the charge was not deleted');
      }
    }
    throw new Error('Draft statements still include the charge after recalculation, the charge was not deleted');
  };

  const deleteChargeOnce = async (charge: Charge): Promise<Charge> => {
    const chargeBeforeDeletion = await get<Charge>(getQuery, charge.id);
    if (isChargeDeletionBlockedByAffectation(chargeBeforeDeletion)) {
      throw new ChargeDeletionBlockedByAffectationError(charge.id);
    }
    const [chargePostings, chargeStatements] = await Promise.all([
      fetchPostings('byCharge', charge.id, undefined, CHARGE_DELETION_POSTINGS_QUERY),
      fetchChargeStatements('byCharge', charge.id),
    ]);
    if (isChargeDeletionBlockedByStatement(chargePostings, chargeStatements)) {
      throw new ChargeDeletionBlockedByStatementError(charge.id);
    }
    await reverseChargePostings(chargePostings, createPosting, updatePosting);
    const statementsRecalculated = await recalculateLinkedDraftStatements(charge.id);
    const deletedCharge = await deleteAndHideEntity<Charge, DeleteMutationVariables>(
      chargeBeforeDeletion,
      deleteMutation
    );
    if (statementsRecalculated) {
      queryClient.invalidateQueries({ queryKey: clientStatementsQueryKey(clientId) });
    }
    return deletedCharge;
  };

  const deleteCharge = async (charge: Charge): Promise<Charge> => {
    const deletedCharge = await deleteChargeOnce(charge).catch((error) => {
      if (!isConditionalCheckFailedGraphQLError(error)) {
        throw error;
      }
      return deleteChargeOnce(charge);
    });
    dispatch({ type: 'DELETE_CHARGE', payload: { id: charge.id } });
    invalidateOpenInvoices(queryClient, clientId);
    return deletedCharge;
  };

  const values = useMemo(
    () => ({
      ...state,
      ...classifyCharges(state.charges ?? []),
      createCharge,
      processOcr,
      getCharge,
      updateCharge,
      affectCharge,
      deleteCharge,
      setFetchCharges,
      fetchAndSetCharges,
      getChargesLinkedToFile,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <ChargeContext.Provider value={values}>{children}</ChargeContext.Provider>;
};

export const useCharges = (): ChargeContext => {
  const firstRender = useRef<boolean>(false);
  const context = useContext<ChargeContext | null>(ChargeContext);

  if (isNil(context)) {
    throw new Error('`useCharges` hook must be used within a `ChargeContext` component');
  }
  useEffect(() => {
    if (!firstRender.current && isNil(context.charges)) {
      context.setFetchCharges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstRender]);
  return context;
};
